import { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui';

interface TopBarProps {
  apolloConnected?: boolean;
  mondayConnected?: boolean;
  onRefresh?: (options?: { force?: boolean }) => void;
  isRefreshing?: boolean;
  lastSyncedAt?: number | null;
}

function formatTimeAgo(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function TopBar({
  apolloConnected = true,
  mondayConnected = true,
  onRefresh,
  isRefreshing = false,
  lastSyncedAt,
}: TopBarProps) {
  // Re-render every 30s to keep "time ago" fresh
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick(t => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex h-14 items-center justify-between border-b border-zinc-800 bg-zinc-900 px-6">
      <div className="flex items-center gap-3">
        {onRefresh && (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onRefresh({ force: true })}
              disabled={isRefreshing}
              className="gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Syncing...' : 'Refresh'}
            </Button>
            {lastSyncedAt && !isRefreshing && (
              <span className="text-xs text-zinc-600">
                Synced {formatTimeAgo(lastSyncedAt)}
              </span>
            )}
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 rounded-lg bg-zinc-800/50 px-3 py-1.5" title={apolloConnected ? 'Apollo API connected' : 'Apollo API disconnected'}>
          <span className={`h-2 w-2 rounded-full ${apolloConnected ? 'bg-emerald-500' : 'bg-zinc-500'}`} />
          <span className="text-sm text-zinc-300">Apollo</span>
        </div>
        <div className="flex items-center gap-2 rounded-lg bg-zinc-800/50 px-3 py-1.5" title={mondayConnected ? 'Monday.com API connected' : 'Monday.com API disconnected'}>
          <span className={`h-2 w-2 rounded-full ${mondayConnected ? 'bg-emerald-500' : 'bg-zinc-500'}`} />
          <span className="text-sm text-zinc-300">Monday</span>
        </div>
      </div>
    </div>
  );
}
